import React, { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { X, Download, BookOpen, ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

const COPY = {
  pt: {
    overline: "Espera aí ! 👋",
    title: "Antes de sair, leva o guia.",
    body: "50 expressões francesas que todo brasileiro deveria conhecer — em PDF, 12 páginas, 100% grátis. As idiomáticas que separam o francês de escola do francês de verdade.",
    cta: "Quero o guia em PDF",
    dismiss: "Agora não",
    tag: "PDF · 12 páginas",
  },
  en: {
    overline: "Wait a minute! 👋",
    title: "Before you go, grab the guide.",
    body: "50 French expressions every Brazilian should know — 12-page PDF, 100% free. The idioms that separate textbook French from real-life French.",
    cta: "Get the PDF guide",
    dismiss: "Not now",
    tag: "PDF · 12 pages",
  },
  fr: {
    overline: "Un instant ! 👋",
    title: "Avant de partir, prenez le guide.",
    body: "50 expressions françaises que tout Brésilien devrait connaître — PDF 12 pages, 100 % offert. Les idiomes qui séparent le français scolaire du français réel.",
    cta: "Je veux le guide PDF",
    dismiss: "Plus tard",
    tag: "PDF · 12 pages",
  },
};

const STORAGE_KEY = "npdl_exit_intent_dismissed_v1";
const SESSION_KEY = "npdl_exit_intent_shown_v1";

export default function ExitIntent() {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip if user already dismissed it in the last 7 days
    try {
      const dismissedAt = localStorage.getItem(STORAGE_KEY);
      if (dismissedAt) {
        const days = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (days < 7) return;
      }
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch (_e) { void _e;
      /* storage may be disabled */
    }

    // Desktop: trigger on mouse leaving the top of the viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        show();
      }
    };
    // Mobile: trigger after 25 s of attentive scrolling
    let mobileTimer = null;
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (isTouch) {
      mobileTimer = setTimeout(show, 25000);
    } else {
      document.addEventListener("mouseout", onMouseOut);
    }

    function show() {
      setOpen(true);
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch (_e) { void _e;
        /* ignore */
      }
      track("exit_intent_shown", { lang });
      document.removeEventListener("mouseout", onMouseOut);
      if (mobileTimer) clearTimeout(mobileTimer);
    }

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [lang]);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (_e) { void _e;
      /* ignore */
    }
    track("exit_intent_dismissed", { lang });
  };

  const goToContact = () => {
    track("exit_intent_cta_clicked", { lang, target: "lead_magnet" });
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (_e) { void _e;
      /* ignore */
    }
    setOpen(false);
    setTimeout(() => {
      const el = document.querySelector("#guia-50-expressoes");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.location.hash = "#guia-50-expressoes";
    }, 100);
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 sm:px-6"
    >
      <button
        aria-label="Fechar"
        data-testid="exit-intent-backdrop"
        onClick={close}
        className="absolute inset-0 bg-[#1e2a5a]/70 backdrop-blur-sm"
      />
      <div className="relative max-w-lg w-full bg-[#f4f1ea] border border-[#e5e3db] rounded-2xl shadow-2xl p-8 sm:p-10 animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={close}
          aria-label="Fechar"
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-[#5c5c5c] hover:bg-[#e5e3db] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="inline-flex items-center gap-2 rounded-full bg-[#1e2a5a]/5 px-3 py-1.5 text-xs text-[#1e2a5a]">
          <BookOpen className="h-3.5 w-3.5" /> {t.tag}
        </div>
        <p className="overline text-[#d9381e] mt-4">{t.overline}</p>
        <h2
          id="exit-intent-title"
          className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-[#1e2a5a] leading-[1.1]"
        >
          {t.title}
        </h2>
        <p className="mt-5 text-base text-[#3a3a3a] leading-relaxed">{t.body}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={goToContact}
            data-testid="exit-intent-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#d9381e] px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#1e2a5a]"
          >
            <Download className="h-4 w-4" />
            {t.cta}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={close}
            data-testid="exit-intent-dismiss"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1e2a5a]/30 px-6 py-3.5 text-sm text-[#5c5c5c] hover:border-[#1e2a5a] hover:text-[#1e2a5a] transition-colors"
          >
            {t.dismiss}
          </button>
        </div>
      </div>
    </div>
  );
}
